import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { KeycloakProfile } from "keycloak-js";

import { Button } from "@/shared/components/ui/Button";
import { fetchAuthUser } from "@/features/auth/api/auth-client";
import { PERMISSION_PANEL_ENABLED } from "@/features/auth/access-config";
import { useToast } from "./ToastContext";

/** localStorage key for the dev/QA permission overrides (panel-managed). */
const PERMISSION_OVERRIDES_KEY = "dev:permissionOverrides";

function readStoredOverrides(): Record<string, boolean> | null {
  if (!PERMISSION_PANEL_ENABLED) return null;
  try {
    const raw = localStorage.getItem(PERMISSION_OVERRIDES_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : null;
  } catch {
    return null;
  }
}
import {
  clearKeycloakSession,
  getKeycloakDisplayName,
  getKeycloakSecureContextError,
  initializeKeycloak,
  loginToKeycloak,
  loadKeycloakProfile,
  logoutFromKeycloak,
  refreshKeycloakToken,
  keycloak,
} from "@/shared/services/keycloak";

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  displayName: string | null;
  profile: KeycloakProfile | null;
  /** Granular Django permission map from `/auth/user/` (`{codename: bool}`). */
  permissions: Record<string, boolean>;
  /**
   * True only when the user explicitly holds `codename`. Fail-closed: returns
   * false while permissions are loading, on fetch error, and for unknown keys.
   */
  can: (codename: string) => boolean;
  /**
   * Dev/QA permission overrides (gated by VITE_PERMISSIONS_PANEL). When non-null,
   * `can()` reads this map instead of the real one. Always null in production.
   */
  permissionOverrides: Record<string, boolean> | null;
  setPermissionOverrides: (next: Record<string, boolean> | null) => void;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

function FullScreenAuthState({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-primary px-6">
      <div className="flex items-center gap-3 rounded-xl border border-border-primary bg-background-card px-5 py-4 shadow-sm">
        <div className="h-2.5 w-2.5 rounded-full bg-accent-primary animate-pulse" />
        <p className="text-sm font-medium text-text-secondary">{label}</p>
      </div>
    </div>
  );
}

function FullScreenSignedOutState({
  onLogin,
  authError,
}: {
  onLogin: () => Promise<void>;
  authError?: string | null;
}) {
  const secureContextError = getKeycloakSecureContextError();
  const blockingError = secureContextError ?? authError ?? null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-primary px-6">
      <div className="w-full max-w-md rounded-2xl border border-border-primary bg-background-card p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-text-primary">
          {blockingError ? "Cannot sign in" : "Signed out"}
        </h1>
        <p className="mt-2 text-sm text-text-secondary">
          {blockingError ??
            "Your Keycloak session has ended. Sign in again to continue using the planner."}
        </p>
        <Button
          className="mt-6 w-full"
          disabled={Boolean(blockingError)}
          onClick={() => void onLogin()}
        >
          Sign in with Keycloak
        </Button>
      </div>
    </div>
  );
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { notify } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [profile, setProfile] = useState<KeycloakProfile | null>(null);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  // Gate the initial authenticated render until the permission map has loaded
  // (or failed closed). Without this, `RequirePermission` gates would evaluate
  // can() against the empty map between Keycloak init and /auth/user/ returning,
  // flashing access-denied / an empty nav to permitted users. Only blocks the
  // FIRST load — stays true across token refreshes so refreshes never re-blank
  // the app (permissions just update in place).
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  // Dev/QA only (VITE_PERMISSIONS_PANEL). null in prod — see readStoredOverrides.
  const [permissionOverrides, setPermissionOverridesState] = useState<Record<
    string,
    boolean
  > | null>(() => readStoredOverrides());
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const syncIdentity = async () => {
      const loadedProfile = await loadKeycloakProfile();
      if (cancelled) return;

      setDisplayName(getKeycloakDisplayName());
      setProfile(loadedProfile);
      setIsAuthenticated(Boolean(keycloak.authenticated));
      setIsLoading(false);

      // Load the granular permission map used by can(). Fail-closed: any error
      // leaves an empty map, so every gated surface stays hidden/disabled.
      try {
        const user = await fetchAuthUser();
        if (cancelled) return;
        setPermissions(user.permissions);
      } catch {
        if (cancelled) return;
        setPermissions({});
      } finally {
        // Permissions are now populated or failed-closed — release the initial
        // render gate either way (fail-closed keeps gated surfaces hidden).
        if (!cancelled) setPermissionsLoaded(true);
      }
    };

    keycloak.onAuthSuccess = () => {
      void syncIdentity();
    };
    keycloak.onAuthRefreshSuccess = () => {
      void syncIdentity();
    };
    keycloak.onAuthLogout = () => {
      clearKeycloakSession();
      if (cancelled) return;
      setDisplayName(null);
      setProfile(null);
      setPermissions({});
      setPermissionsLoaded(false);
      setIsAuthenticated(false);
      setIsLoading(false);
    };
    keycloak.onTokenExpired = () => {
      void refreshKeycloakToken().catch(() => {
        clearKeycloakSession();
      });
    };

    void initializeKeycloak()
      .then(async (authenticated) => {
        if (cancelled) return;
        if (!authenticated) {
          setAuthError(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
        setAuthError(null);
        await syncIdentity();
      })
      .catch((error: unknown) => {
        clearKeycloakSession();
        if (cancelled) return;

        const message =
          error instanceof Error ? error.message : "Authentication failed";
        setAuthError(message);
        setIsAuthenticated(false);
        setIsLoading(false);
        setDisplayName(null);
        setProfile(null);
        notify("error", message);
      });

    return () => {
      cancelled = true;
      keycloak.onAuthSuccess = undefined;
      keycloak.onAuthRefreshSuccess = undefined;
      keycloak.onAuthLogout = undefined;
      keycloak.onTokenExpired = undefined;
    };
  }, [notify]);

  const login = useCallback(async () => {
    try {
      await loginToKeycloak();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Sign-in failed";
      setAuthError(message);
      notify("error", message);
    }
  }, [notify]);

  const logout = useCallback(async () => {
    await logoutFromKeycloak();
  }, []);

  const setPermissionOverrides = useCallback(
    (next: Record<string, boolean> | null) => {
      setPermissionOverridesState(next);
      if (!PERMISSION_PANEL_ENABLED) return;
      try {
        if (next) {
          localStorage.setItem(PERMISSION_OVERRIDES_KEY, JSON.stringify(next));
        } else {
          localStorage.removeItem(PERMISSION_OVERRIDES_KEY);
        }
      } catch {
        // ignore storage failures (private mode, quota, etc.)
      }
    },
    [],
  );

  const can = useCallback(
    (codename: string) => {
      // Dev overrides win when active; otherwise the real (fail-closed) map.
      const source = permissionOverrides ?? permissions;
      return source[codename] === true;
    },
    [permissionOverrides, permissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading,
      displayName,
      profile,
      permissions,
      can,
      permissionOverrides,
      setPermissionOverrides,
      login,
      logout,
    }),
    [
      displayName,
      isAuthenticated,
      isLoading,
      login,
      logout,
      profile,
      permissions,
      can,
      permissionOverrides,
      setPermissionOverrides,
    ],
  );

  if (isLoading) {
    return <FullScreenAuthState label="Signing in with Keycloak..." />;
  }

  if (!isAuthenticated) {
    return <FullScreenSignedOutState onLogin={login} authError={authError} />;
  }

  // Authenticated, but hold the gated app until permissions resolve once, so no
  // RequirePermission gate evaluates against the empty (still-loading) map.
  if (!permissionsLoaded) {
    return <FullScreenAuthState label="Loading your workspace..." />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
